// Offline mirror of the deterministic parts of backend/calc.py. Computes ONLY the work
// entry's `expected_cents` using integer-cent / basis-point arithmetic so an offline-created
// Work card can show the correct expected earnings instead of a misleading $0.00.
//
// The SERVER remains authoritative: on reconnect the outbox replays the write and the
// server's record (with its own snapshot) overwrites the local one. This module never
// touches GST/BAS reporting — GST does not change `expected_cents` for the Work card.
//
// If the amount cannot be reproduced confidently from the locally-seeded agreement, the
// caller receives `null` and must render a truthful "Pending" state (never $0.00).

// ROUND_HALF_UP away from zero on .5, matching Python Decimal.quantize(ROUND_HALF_UP).
function roundHalfUp(value) {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

// base_cents * (bps / 10000), rounded half up to the cent. base/bps are integers.
function applyPercentage(baseCents, bps) {
  return roundHalfUp((baseCents * bps) / 10000);
}

function dentistLabDeduction(labTreatment, labFeesCents, dentistLabShareBps) {
  const lab = Math.trunc(labFeesCents || 0);
  if (labTreatment === "practice_pays") return 0;
  if (labTreatment === "dentist_pays") return lab;
  if (labTreatment === "shared") return applyPercentage(lab, Math.trunc(dentistLabShareBps || 0));
  return 0;
}

const asInt = (v) => Math.trunc(Number(v) || 0);
const asNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Pick the agreement version applicable to `date` for a practice, mirroring the server's
// current_agreement(): latest version whose effective window contains the date, else v1.
export function pickAgreementForDate(versions, practiceId, date) {
  let list = (versions || []).filter((v) => String(v.practice_id) === String(practiceId));
  if (!list.length) return null;
  const ver = (v) => Number(v.version ?? v.version_number ?? 0);
  list = [...list].sort((a, b) => ver(a) - ver(b));
  if (!date) return list[list.length - 1];
  const d = String(date).slice(0, 10);
  const applicable = list.filter((v) => {
    const start = v.effective_start ? String(v.effective_start).slice(0, 10) : null;
    const end = v.effective_end ? String(v.effective_end).slice(0, 10) : null;
    if (start && start > d) return false;
    if (end && end < d) return false;
    return true;
  });
  if (applicable.length) return applicable.sort((a, b) => ver(a) - ver(b))[applicable.length - 1];
  return list[0];
}

// Returns integer cents, or null when it cannot be reproduced confidently (=> "Pending").
export function computeExpectedCents(agreement, inputs) {
  if (!agreement) return null;
  const method = agreement.calc_method;
  if (!method) return null;

  const billings = asInt(inputs.billings_cents);
  const collections = asInt(inputs.collections_cents);
  const labFees = asInt(inputs.lab_fees_cents);
  const other = asInt(inputs.other_deductions_cents);
  const hours = asNum(inputs.hours);
  const days = asNum(inputs.days);

  const labTreatment = agreement.lab_treatment || "practice_pays";
  const labDeduction = dentistLabDeduction(labTreatment, labFees, agreement.dentist_lab_share_bps);

  switch (method) {
    case "percentage_billings":
      return applyPercentage(billings - other, asInt(agreement.percentage_bps));
    case "percentage_collections":
      return applyPercentage(collections - other, asInt(agreement.percentage_bps));
    case "percentage_after_lab":
      return applyPercentage(billings - labDeduction - other, asInt(agreement.percentage_bps));
    case "hourly":
      return roundHalfUp(asInt(agreement.hourly_rate_cents) * hours);
    case "daily":
      return roundHalfUp(asInt(agreement.daily_rate_cents) * days);
    case "fixed":
      return asInt(agreement.fixed_amount_cents);
    case "custom":
      return inputs.custom_expected_cents != null
        ? asInt(inputs.custom_expected_cents)
        : asInt(agreement.fixed_amount_cents);
    case "adjusted_billing": {
      if (agreement.service_fee_bps == null) return null;
      const serviceBps = asInt(agreement.service_fee_bps);
      const basis = agreement.service_fee_basis || "practice_share";
      const extra = asInt(inputs.extra_deductions_cents);
      const deductionsTotal = labDeduction + other + extra;
      const adjusted = billings - deductionsTotal;
      const dentistBps = basis === "practice_share" ? Math.max(10000 - serviceBps, 0) : serviceBps;
      const dentistBase = applyPercentage(adjusted, dentistBps);
      if (agreement.gst_model === "components") {
        const serviceFeeCents = adjusted - dentistBase;
        const serviceFeeGst = agreement.service_fee_gst_enabled ? applyPercentage(serviceFeeCents, 1000) : 0;
        const dentistNet = dentistBase - serviceFeeGst;
        const dentistGst = agreement.dentist_gst_enabled ? applyPercentage(dentistNet, 1000) : 0;
        return dentistNet + dentistGst;
      }
      return dentistBase; // legacy: expected_cents is the pre-GST dentist base
    }
    default:
      return null;
  }
}
